import { useEffect } from "react";

/**
 * Sets <title>, meta description, canonical link, og:title, og:description, og:url
 * and an optional JSON-LD <script> tag. Restores previous values on unmount.
 */
export default function useSeo({ title, description, canonical, jsonLd, ogType, ogImage }) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const setMeta = (selector, attr, value) => {
      if (!value) return null;
      let el = document.querySelector(selector);
      const created = !el;
      if (!el) {
        el = document.createElement("meta");
        const [, key, val] = selector.match(/\[(.*?)="(.*?)"\]/) || [];
        if (key && val) el.setAttribute(key, val);
        document.head.appendChild(el);
      }
      const prev = el.getAttribute(attr);
      el.setAttribute(attr, value);
      return { el, attr, prev, created };
    };

    const setLink = (rel, href) => {
      if (!href) return null;
      let el = document.querySelector(`link[rel="${rel}"]`);
      const created = !el;
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      const prev = el.getAttribute("href");
      el.setAttribute("href", href);
      return { el, prev, created };
    };

    const restorers = [];
    if (description) {
      const r = setMeta('meta[name="description"]', "content", description);
      if (r) restorers.push(() => (r.created ? r.el.remove() : r.el.setAttribute(r.attr, r.prev || "")));
      const og = setMeta('meta[property="og:description"]', "content", description);
      if (og) restorers.push(() => (og.created ? og.el.remove() : og.el.setAttribute(og.attr, og.prev || "")));
    }
    if (title) {
      const og = setMeta('meta[property="og:title"]', "content", title);
      if (og) restorers.push(() => (og.created ? og.el.remove() : og.el.setAttribute(og.attr, og.prev || "")));
    }
    if (canonical) {
      const link = setLink("canonical", canonical);
      if (link) restorers.push(() => (link.created ? link.el.remove() : link.el.setAttribute("href", link.prev || "")));
      const og = setMeta('meta[property="og:url"]', "content", canonical);
      if (og) restorers.push(() => (og.created ? og.el.remove() : og.el.setAttribute(og.attr, og.prev || "")));
    }
    if (ogType) {
      const og = setMeta('meta[property="og:type"]', "content", ogType);
      if (og) restorers.push(() => (og.created ? og.el.remove() : og.el.setAttribute(og.attr, og.prev || "")));
    }
    if (ogImage) {
      const og = setMeta('meta[property="og:image"]', "content", ogImage);
      if (og) restorers.push(() => (og.created ? og.el.remove() : og.el.setAttribute(og.attr, og.prev || "")));
    }

    let jsonLdEl = null;
    if (jsonLd) {
      // Prerendered HTML (served to crawlers) already contains a dynamic
      // JSON-LD <script data-dynamic="true"> captured at build time.
      // Reuse it on hydration instead of appending a second one — otherwise
      // Google Search Console flags "Duplicate field" on rich-result pages
      // (e.g. FAQPage) once Googlebot's renderer runs the React bundle.
      const existing = document.querySelector(
        'script[type="application/ld+json"][data-dynamic="true"]',
      );
      jsonLdEl = existing || document.createElement("script");
      jsonLdEl.type = "application/ld+json";
      jsonLdEl.dataset.dynamic = "true";
      jsonLdEl.textContent = JSON.stringify(jsonLd);
      if (!existing) document.head.appendChild(jsonLdEl);
    }

    return () => {
      document.title = prevTitle;
      restorers.forEach((r) => r());
      if (jsonLdEl) jsonLdEl.remove();
    };
  }, [title, description, canonical, jsonLd, ogType, ogImage]);
}
